import React, { useState } from "react";
import { useI18n } from "react-simple-i18n";
import { styled } from "@mui/material/styles";
import { Box, Checkbox, FormControl, Radio, Stack, Typography } from "@mui/material";

import { restApi } from "../../provider/restApi";
import { Layout } from "../../components/layout/layout";
import { Divider } from "../../components/divider/divider";
import { FirstButton } from "../../components/buttons/buttons";
import { TransparentTextField } from "../../components/inputs/textfield";
import { GlobalFirstSection, GlobalSpacing } from "../../components/layout/styles";
import { apiNotification } from "../../services/apiNotification";
import { useCustomNavigate } from "../../provider/navigate";
import { MailVerifyChecker } from "./mailVerifyChecker";
import { useGlobalContext } from "../../provider";

interface RegisterData {
	fullName: string;
	email: string;
	password: string;
	userType: string;
	agreeTerms: boolean;

	errorName: boolean
	errorEmail: boolean
	errorPassword: boolean
	errorUserType: boolean
	errorAgreeTerms: boolean
}

const initRegisterData: RegisterData = {
	fullName: "",
	email: "",
	password: "",
	userType: "",
	agreeTerms: false,

	errorName: false,
	errorEmail: false,
	errorPassword: false,
	errorUserType: false,
	errorAgreeTerms: false,
}

const Register = () => {
	const { t } = useI18n();
	const { customNavigate } = useCustomNavigate();
	const [, { dispatch }]: GlobalContextType = useGlobalContext();
	const [registerData, setRegisterData] = useState<RegisterData>(initRegisterData);
	const [isVerifyCode, setIsVerifyCode] = useState(false);
	const [verifyCode, setVerifyCode] = useState("");

	const updateRegisterData = (data: Partial<RegisterData>) => {
		setRegisterData({ ...registerData, ...data });
	}

	const checkValidate = (data: RegisterData): boolean => {
		if (!data.fullName) return false;
		if (!data.email) return false;
		if (!data.password) return false;
		if (!data.userType) return false;
		if (!data.agreeTerms) return false;

		return true;
	}

	const handleSignUp = async () => {
		try {
			const validate = checkValidate(registerData);

			if (!validate) {
				updateRegisterData({
					errorName: !registerData.fullName,
					errorEmail: !registerData.email,
					errorPassword: !registerData.password,
					errorUserType: !registerData.userType,
					errorAgreeTerms: !registerData.agreeTerms,
				})

				return false;
			}
			const isAuthorized:any="true";
			const data: SignUpProps = {
				name: registerData.fullName,
				email: registerData.email,
				password: registerData.password,
				userType: registerData.userType,
			}

			dispatch({ loading: true });
			const resData = await restApi.signup(data);
			localStorage.setItem("name", resData.userData.name)
			localStorage.setItem("email", resData.userData.email)
			localStorage.setItem("isAuthorized", isAuthorized)
			localStorage.setItem("UserId",resData.userData.id);
			setVerifyCode(resData.verifyCode);
			setIsVerifyCode(true);
			dispatch({ loading: false });
		} catch (error) {
			dispatch({ loading: false })
			apiNotification(error, "Register failed!");
		}
	}

	const signUpData: SignUpProps = {
		name: registerData.fullName,
		email: registerData.email,
		password: registerData.password,
		userType: registerData.userType,
	}

	return (
		<React.Fragment>
			{!isVerifyCode && (
				<Layout>
					<GlobalSpacing>
						<RegisterWrapper>
							<RegisterForm>
								<Typography variant="h2">
									{t("signup.title")}
								</Typography>

								<Stack flexDirection="column" gap={1.5}>
									<TransparentTextField required={true}
										variant="standard"
										label={t("inputLabels.fullName")}
										value={registerData.fullName}
										onChange={({ target }: any) => { updateRegisterData({ fullName: target.value, errorName: false }) }}
										error={registerData.errorName}
									/>

									<TransparentTextField required={true}
										variant="standard"
										label={t("inputLabels.email")}
										value={registerData.email}
										onChange={({ target }: any) => { updateRegisterData({ email: target.value, errorEmail: false }) }}
										error={registerData.errorEmail}
									/>

									<TransparentTextField required={true}
										variant="standard"
										label={t("inputLabels.password")}
										value={registerData.password} type="password"
										onChange={({ target }: any) => { updateRegisterData({ password: target.value, errorPassword: false }) }}
										error={registerData.errorPassword}
									/>

									<UserTypeWrapper error={registerData.errorUserType}>
										<UserTypeItem>
											<Radio value="student"
												checked={registerData.userType === 'student'}
												onChange={({ target }: any) => { updateRegisterData({ userType: target.value, errorUserType: false }); }}
											/>

											<Typography variant="body1">{t("inputLabels.student")}</Typography>
										</UserTypeItem>


										<UserTypeItem>
											<Radio value="instructor"
												checked={registerData.userType === 'instructor'}
												onChange={({ target }: any) => { updateRegisterData({ userType: target.value, errorUserType: false }); }}
											/>
											<Typography variant="body1">{t("inputLabels.instructor")}</Typography>
										</UserTypeItem>
									</UserTypeWrapper>

									<AgreeTermsAndPolice error={registerData.errorAgreeTerms}>
										<Checkbox size="small"
											checked={registerData.agreeTerms}
											onChange={() => { updateRegisterData({ agreeTerms: !registerData.agreeTerms, errorAgreeTerms: false }); }}
										/>

										<Typography variant="body1" component="h6">
											{t("signup.agree")}
											<Typography variant="body1" onClick={customNavigate("/terms")}>{t("signup.terms")}</Typography>
											{t("signup.and")}
											<Typography variant="body1" onClick={customNavigate("/privacy")}>{t("signup.policy")}</Typography>
										</Typography>
									</AgreeTermsAndPolice>
								</Stack>

								<FirstButton onClick={handleSignUp}>
									<Typography>{t("buttons.signup")}</Typography>
								</FirstButton>

								<Divider />
								<AlreadyHaveAccount>
									<Typography variant="body1" component="h6">{t("signup.haveAccount")}</Typography>
									<Typography variant="body1" onClick={customNavigate("/login")}>{t("buttons.login")}</Typography>
								</AlreadyHaveAccount>
							</RegisterForm>
						</RegisterWrapper>
					</GlobalSpacing>
				</Layout >
			)}

			{isVerifyCode && (
				<MailVerifyChecker data={signUpData} verifyCode={verifyCode}/>
			)}
		</React.Fragment>
	)
}

const RegisterWrapper = styled(GlobalFirstSection)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "center",
	padding: "50px 0px",
}))

const RegisterForm = styled(Box)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	textAlign: "center",
	gap: theme.spacing(4),

	maxWidth: 550,
	width: "100%",
}))

const UserTypeWrapper = styled(FormControl)(({ theme, error = false }) => ({
	display: "flex",
	flexDirection: "row",
	textAlign: "center",
	gap: theme.spacing(5),

	p: {
		color: error ? theme.palette.common.fourthColor : theme.palette.common.firstColor,
	},

	".MuiSvgIcon-root": {
		color: error ? theme.palette.common.fourthColor : "unset",
	},

	[theme.breakpoints.down("md")]: {
		gap: theme.spacing(3),
	}
}))

const UserTypeItem = styled(Box)(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	alignItems: "center",
}))

const AgreeTermsAndPolice = styled(FormControl)(({ theme, error = false }) => ({
	display: "flex",
	flexDirection: "row",
	alignItems: "center",

	'.MuiCheckbox-root': {
		color: error ? theme.palette.common.fourthColor : theme.palette.common.firstColor,
	},

	h6: {
		display: "flex",
		flexDirection: "row",
		gap: theme.spacing(1),
		color: error ? theme.palette.common.fourthColor : theme.palette.common.firstColor,
	},

	p: {
		cursor: "pointer",
		color: error ? theme.palette.common.fourthColor : theme.palette.common.thirdColor,
	},


}))

const AlreadyHaveAccount = styled(Box)(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	alignItems: "center",
	gap: theme.spacing(1),

	p: {
		cursor: "pointer",
		color: theme.palette.common.thirdColor,
	}
}))

export { Register }